// services/frontend/src/components/composed/CommentThread/CommentThread.tsx
// T020-T022: Add data-critique-id and selectedCritiqueId support (040-critique-url-state)
// T068: Add EditSuggestionPreview integration (043-critique-edit-suggestions)
// Feature 050: Add MarkupAttachmentPreview integration (050-critique-markup-integration)
// Feature 081: Version-aware attachment rendering

import { useState, useMemo } from 'react';
import { Critique } from '../../../types/critique';
import { Photo } from '../../../types/photo';
import { Avatar } from '../../atoms/Avatar/Avatar';
import { Text } from '../../atoms/Text/Text';
import { Button } from '../../atoms/Button/Button';
import { MarkdownRenderer } from '../../MarkdownRenderer';
import { cn } from '../../../utils/cn';
import { EditSuggestionPreview } from '../EditSuggestionPreview/EditSuggestionPreview';
import { MarkupAttachmentPreview } from '../../markup/MarkupAttachmentPreview';
import { MarkupPreviewModal } from '../../modals/MarkupPreviewModal';
import logger from '../../../utils/logger';
import { formatRelativeTime } from '../../../utils/dateFormat';
import { buildPhotoFromVersion } from '../../../utils/buildPhotoFromVersion';

export interface CommentThreadProps {
  critique: Critique;
  photo?: Photo; // T068: For edit suggestion preview (043-critique-edit-suggestions)
  onReply?: (critiqueId: string) => void;
  maxDepth?: number;
  children?: React.ReactNode; // For reply forms or nested content
  selectedCritiqueId?: string | null; // NEW: For URL state highlighting (040-critique-url-state)
  onCommentClick?: (commentId: string) => void; // T037: For URL state updates (040-critique-url-state)
  replyingTo?: string | null; // Current critique being replied to (for nested reply forms)
  renderReplyForm?: (critiqueId: string) => React.ReactNode; // Function to render reply form for nested comments
}

export function CommentThread({
  critique,
  photo,
  onReply,
  maxDepth = 3,
  children,
  selectedCritiqueId,
  onCommentClick,
  replyingTo,
  renderReplyForm
}: CommentThreadProps) {
  const hasReplies = critique.children && critique.children.length > 0;
  const canReply = critique.threadDepth < maxDepth;
  const isReplying = replyingTo === critique.id;

  // T021-T022: Highlight selected critique (040-critique-url-state)
  const isSelected = selectedCritiqueId === critique.id;

  // Feature 050: Markup preview modal state
  const [selectedMarkupIndex, setSelectedMarkupIndex] = useState<number | null>(null);

  // Feature 081: Build version-aware photo for attachment rendering
  // If critique has version data, use that version's variants instead of current photo
  const attachmentPhoto = useMemo(() => {
    if (!photo) return undefined;

    // If critique has version data, build photo from version
    if (critique.photoVersion) {
      logger.debug('CommentThread', 'Using version-aware photo for attachments', {
        critiqueId: critique.id,
        versionNumber: critique.photoVersion.versionNumber,
        isCurrent: critique.photoVersion.isCurrent,
      });
      return buildPhotoFromVersion(photo, critique.photoVersion);
    }

    // No version data - use current photo (shouldn't happen with new API)
    return photo;
  }, [photo, critique.photoVersion, critique.id]);

  // Handle shape reference click - open preview modal for specific shape
  const handleShapeReferenceClick = (shapeNumber: number) => {
    if (!critique.markup || critique.markup.length === 0) return;

    // Find the markup that contains the referenced shape
    const markupIndex = critique.markup.findIndex((m: any) => {
      const shapes = m.editData?.shapes || [];
      return shapes.some((s: any) => s.number === shapeNumber);
    });

    if (markupIndex >= 0) {
      logger.info('CommentThread', 'Opening markup preview for shape:', shapeNumber);
      setSelectedMarkupIndex(markupIndex);
    }
  };

  return (
    <article
      data-critique-id={critique.id} // T020: For scroll targeting (040-critique-url-state)
      onClick={(e) => {
        // Stop propagation to prevent parent comments from capturing clicks
        e.stopPropagation();
        onCommentClick?.(critique.id);
      }}
      className={cn(
        'border-l-2 pl-4 mb-4 transition-colors',
        isSelected
          ? 'border-primary bg-surface-2' // T022: Highlight when selected (040-critique-url-state)
          : 'border-border',
        onCommentClick && 'cursor-pointer hover:bg-surface-1' // T036: Make clickable when callback provided (040-critique-url-state)
      )}
      aria-label={`Comment by ${critique.displayName || critique.username}`}
    >
      {/* Comment header */}
      <div className="flex items-center gap-2 mb-2">
        <Avatar
          src={undefined}
          fallback={(critique.displayName || critique.username).charAt(0).toUpperCase()}
          size="sm"
        />
        <Text className="font-semibold text-foreground">
          {critique.displayName || critique.username}
        </Text>
        <Text variant="muted" className="text-sm">
          {formatRelativeTime(critique.createdAt)}
        </Text>
      </div>

      {/* Comment content - rendered markdown from backend */}
      <MarkdownRenderer
        content={critique.content}
        className="text-foreground mb-3"
        photo={photo}
        markup={critique.markup}
        onShapeReferenceClick={handleShapeReferenceClick}
      />

      <div className="mb-3 flex flex-wrap gap-2">
        {/* T068: Edit suggestion preview (043-critique-edit-suggestions) */}
        {/* Feature 081: Use attachmentPhoto for version-aware rendering */}
        {critique.edit && attachmentPhoto && (
          <div className="mb-3">
            <EditSuggestionPreview
              photo={attachmentPhoto}
              edit={critique.edit}
            />
          </div>
        )}

        {/* Feature 050: Markup annotation previews (050-critique-markup-integration) */}
        {/* Feature 081: Use attachmentPhoto for version-aware rendering */}
        {critique.markup && Array.isArray(critique.markup) && critique.markup.length > 0 && attachmentPhoto && (
            critique.markup.map((markupItem: any, index: number) => (
              <MarkupAttachmentPreview
                key={markupItem.id || index}
                photo={attachmentPhoto}
                shapes={(markupItem.editData as any)?.shapes || []}
                onClick={() => {
                  logger.info('CommentThread', 'Opening markup preview for index:', index);
                  setSelectedMarkupIndex(index);
                }}
              />
            ))
        )}
      </div>

      {/* Reply button */}
      {canReply && onReply && (
        <Button
          variant="outline"
          size="compact"
          onClick={(e) => {
            e.stopPropagation(); // Prevent comment selection when clicking Reply
            onReply(critique.id);
          }}
          className="mb-3"
          aria-label={`Reply to ${critique.displayName || critique.username}`}
        >
          Reply
        </Button>
      )}

      {/* Custom children (reply forms, etc.) */}
      {children}

      {/* Reply form for this comment (when replying and renderReplyForm is provided) */}
      {isReplying && renderReplyForm && renderReplyForm(critique.id)}

      {/* Nested replies */}
      {hasReplies && (
        <div className="mt-3 space-y-3" role="group" aria-label="Replies">
          {critique.children!.map((reply) => (
            <CommentThread
              key={reply.id}
              critique={reply}
              photo={photo} // T068: Propagate photo to nested replies (043-critique-edit-suggestions)
              onReply={onReply}
              maxDepth={maxDepth}
              selectedCritiqueId={selectedCritiqueId} // T021: Propagate to nested replies (040-critique-url-state)
              onCommentClick={onCommentClick} // T036: Propagate click handler to nested replies (040-critique-url-state)
              replyingTo={replyingTo} // Propagate reply state for nested reply forms
              renderReplyForm={renderReplyForm} // Propagate reply form renderer for nested replies
            />
          ))}
        </div>
      )}

      {/* Feature 050: Markup Preview Modal */}
      {/* Feature 081: Use attachmentPhoto for version-aware rendering */}
      {attachmentPhoto && selectedMarkupIndex !== null && critique.markup && critique.markup[selectedMarkupIndex] && (
        <MarkupPreviewModal
          isOpen={true}
          onClose={() => {
            logger.info('CommentThread', 'Closing markup preview modal');
            setSelectedMarkupIndex(null);
          }}
          photo={attachmentPhoto}
          shapes={(critique.markup[selectedMarkupIndex].editData as any)?.shapes || []}
        />
      )}
    </article>
  );
}
